import type { MarzeKategoriiResult, PoleMarze } from "@/api/hooks/useCennik";
import { formatPct, formatPocet, parseDecimal, sklonuj } from "@/lib/format";
import type { KartaMarzi } from "@/components/cennik/marzeKarty";

/** Texty a čísla panelu Nastavenia cien (KOLO 166). */

export const KATEGORII = ["kategóriu", "kategórie", "kategórií"] as const;
export const POLOZIEK = ["položku", "položky", "položiek"] as const;
const ZMENENYCH = [
  "zmenená položka",
  "zmenené položky",
  "zmenených položiek",
] as const;

/** Desatinné miesta, ktoré server prijme (Numeric v DB). */
const MIESTA: Record<PoleMarze, number> = {
  praca_eur_per_mj: 4,
  rezia_pct: 2,
  nadmiera_pct: 2,
  nadmiera_smer_pct: 2,
};

/** Názov stĺpca / poľa — plošné majú dve nadmiery. */
export function nazovPola(pole: PoleMarze, karta: Pick<KartaMarzi, "jednotka" | "polia">): string {
  switch (pole) {
    case "praca_eur_per_mj":
      return `Práca (${karta.jednotka})`;
    case "rezia_pct":
      return "Réžia (%)";
    case "nadmiera_pct":
      return karta.polia.includes("nadmiera_smer_pct")
        ? "Nadmiera uni (%)"
        : "Nadmiera (%)";
    case "nadmiera_smer_pct":
      return "Nadmiera so smerom (%)";
  }
}

/** Krátky názov poľa do aria-label a hlášok. */
export const KRATKY_NAZOV: Record<PoleMarze, string> = {
  praca_eur_per_mj: "Práca",
  rezia_pct: "Réžia",
  nadmiera_pct: "Nadmiera",
  nadmiera_smer_pct: "Nadmiera so smerom",
};

/**
 * Vstup → číslo pre API (text s bodkou), `null` keď neplatí. Čiarka
 * ako bodka (SK zvyk), záporné nie, percentá najviac 100. Zaokrúhli na
 * miesta, ktoré DB drží — inak by server odmietol „10,33333".
 */
export function overHodnotu(pole: PoleMarze, vstup: string): string | null {
  const text = parseDecimal(vstup);
  if (text === "") return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0) return null;
  if (pole !== "praca_eur_per_mj" && n > 100) return null;
  return String(Number(n.toFixed(MIESTA[pole])));
}

/** Hodnota v bunke: práca „10,30 €", percentá „20 %". */
export function formatHodnota(
  pole: PoleMarze,
  v: string | number | null | undefined,
): string {
  if (v === null || v === undefined || v === "") return "—";
  if (pole !== "praca_eur_per_mj") return formatPct(v);
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `${n.toLocaleString("sk-SK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })} €`;
}

/** Hodnota skupiny: jednotná, alebo rozsah „0,30 € – 0,50 €". */
export function formatRozsah(
  pole: PoleMarze,
  hodnoty: readonly (string | null | undefined)[],
): string {
  const cisla = hodnoty
    .filter((v): v is string => v !== null && v !== undefined && v !== "")
    .map(Number);
  if (cisla.length === 0) return "—";
  const min = Math.min(...cisla);
  const max = Math.max(...cisla);
  return max - min < 0.00005
    ? formatHodnota(pole, min)
    : `${formatHodnota(pole, min)} – ${formatHodnota(pole, max)}`;
}

/** Toast po uložení — koľko položiek sa naozaj zmenilo. */
export function popisUlozenia(nazov: string, r: MarzeKategoriiResult): string {
  const z = r.zmenenych_materialov;
  let text = `${nazov} — ${formatPocet(z)} ${sklonuj(z, ZMENENYCH)}`;
  if (r.preskocenych_vlastnych > 0) {
    text += `; ${formatPocet(r.preskocenych_vlastnych)} s vlastnými maržami bez zmeny`;
  }
  return `${text}.`;
}
